import React, {
  createContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useGoogleLogin, googleLogout } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import {
  getCookieData,
  removeAllCookie,
  setCookieData,
  setUserIdTokenInCookie,
} from "../helper/api";
import { UserInfoModel } from "../model/userModel";
import useAuthApi from "../api/auth";

export interface GoogleUser {
  id?: string;
  email: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  picture?: string;
  access_type?: string;
  audience?: string;
  expires_in?: number;
  issued_to?: string;
  scope?: string;
  user_id?: string;
  verified_email?: boolean;
}

export interface AuthContextType {
  user: UserInfoModel;
  isAuthenticated: boolean;
  setUser: React.Dispatch<React.SetStateAction<UserInfoModel>>;
  login: () => void;
  logout: () => void;
  getToken: (code: string, baseURL: string) => void;
  loginTT: (response: UserInfoModel) => void;
 // testLogin: (username: string) => void;
}

const defaultUserInfo: UserInfoModel = {
  user_id: null,
  ldap_uid: "",
  firstname: "",
  lastname: "",
  email_id: "",
  designation: "",
  token: "",
  employee_id: null,
  manager_id: null,
  role_id: null,
  role_name: "",
  department_id: null,
  department_name: "",
  manager_name: "",
  picture: "",
  ipms_id: null,
  iontime_id: null,
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { fetchGetToken1 } = useAuthApi();
  const navigate = useNavigate();
  const baseURL = `${window.location.origin}`;
  // console.log("baseURL", baseURL);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserInfoModel>(defaultUserInfo);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  const logout = () => {
    setUser(defaultUserInfo);
    setIsAuthenticated(false);
    removeAllCookie();
    navigate("/login");
    googleLogout();
  };

  // const getToken = useCallback(async (user_email:string, gtoken: string, path: boolean) => {
  //   try {
  //     const response = await fetchGetToken({ user_email , gtoken});
  //     if (response) {
  //       setCookieData("roleId", String(response?.role_id));
  //       setCookieData("auth_token", response.token);
  //       setCookieData("userAccessId", String(response?.user_id));
  //       setCookieData("iontime_id", String(response?.iontime_id));
  //       setCookieData("ipms_id", String(response?.ipms_id));
  //       setUserIdTokenInCookie(String(response?.user_id));
  //       setCookieData("userData", JSON.stringify(response));
  //       setUser(response);
  //       if (path) {
  //         navigate("/");
  //       }
  //     }
  //   } catch (error) {
  //     /* empty */
  //   }
  // }, []);

  const getToken = useCallback(async (code: string, redirect_uri: string) => {
    try {
      const response = await fetchGetToken1({ code, redirect_uri });
      if (response) {
        setIsAuthenticated(true);
        setCookieData("roleId", String(response?.role_id));
        setCookieData("auth_token", response.token);
        setCookieData("userAccessId", String(response?.user_id));
        setCookieData("iontime_id", String(response?.iontime_id));
        setCookieData("ipms_id", String(response?.ipms_id));
        setCookieData("employee_ID", String(response?.employee_id));
        setUserIdTokenInCookie(String(response?.user_id));
        setCookieData("userData", JSON.stringify(response));
        setUser(response);
        navigate("/");
      }
    } catch (error) {
      setIsAuthenticated(false);
      /* empty */
    }
  }, []);

  const loginTT = (response: UserInfoModel) => {
    try {
      if (response) {
        setIsAuthenticated(true);
        setCookieData("roleId", String(response?.role_id));
        setCookieData("auth_token", response.token);
        setCookieData("userAccessId", String(response?.user_id));
        setCookieData("iontime_id", String(response?.iontime_id));
        setCookieData("ipms_id", String(response?.ipms_id));
        setCookieData("employee_ID", String(response?.employee_id));
        setUserIdTokenInCookie(String(response?.user_id));
        setCookieData("userData", JSON.stringify(response));
        setUser(response);
        navigate("/");
      }
    } catch (error) {
      setIsAuthenticated(false);
      /* empty */
    }
  };
  // // ADD THIS inside AuthProvider, after the loginTT function
  // const testLogin = useCallback(
  //   async (username: string) => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/user/test-login`,
  //         {
  //           method: "POST",
  //           headers: { "Content-Type": "application/json" },
  //           body: JSON.stringify({ username }),
  //         },
  //       );
  //       const data = await response.json();
  //       if (data?.status && data?.data) {
  //         loginTT(data.data); // ✅ reuses existing loginTT — sets cookies, navigates, everything
  //       } else {
  //         console.error("Test login failed:", data?.message);
  //       }
  //     } catch (error) {
  //       console.error("Test login error:", error);
  //     }
  //   },
  //   [loginTT],
  // );
  const login = useGoogleLogin({
    onSuccess: async (response) => {
      // console.log(response);
      const authorizationCode = response.code;
      if (authorizationCode) {
        try {
          getToken(authorizationCode, baseURL);
          if (redirectPath) {
            navigate(redirectPath);
            setRedirectPath(null);
          }
        } catch (error) {
          logout();
        }
      }
    },
    onError: () => {
      logout();
    },
    flow: "auth-code",
    ux_mode: "popup",
    redirect_uri: `${baseURL}`,
  });

  // const login = useGoogleLogin({
  //   onSuccess: async (response) => {
  //     const { access_token, expires_in } = response;

  //     if (access_token) {
  //       try {
  //         const userInfo = await fetch("https://www.googleapis.com/oauth2/v2/userinfo", {
  //           headers: {
  //             Authorization: `Bearer ${access_token}`,
  //           },
  //         }).then((res) => res.json());
  //         console.log(userInfo);
  //         getToken(userInfo.email, access_token, true);
  //         // getToken('anoop.bhargav@ionidea.com', 'ya29.a0AXooCgubaRDg8IWL5nGK2nF4SRhZUNqdrmp3PmyRen9tuoyTWY', true);
  //         setUser(user);
  //         setIsAuthenticated(true);
  //         const expirationTime = new Date().getTime() + expires_in * 1000;
  //         setCookieData("google-token", access_token);
  //         setCookieData("google-token-expiration", expirationTime.toString());

  //         if (redirectPath) {
  //           navigate(redirectPath);
  //           setRedirectPath(null);
  //         }
  //       } catch (error) {
  //         login();
  //         // console.error("Error fetching user info:", error);
  //       }
  //     }
  //   },
  //   onError: () => {
  //     logout();
  //   },
  //   flow: "implicit",
  // });

  useEffect(() => {
    const userData = getCookieData("userData");
    const user = userData ? JSON.parse(userData) : null;
    if (user) {
      setIsAuthenticated(true);
      setUser(user);
      // navigate('/')
    }
    if (!user) logout();
  }, []);

  // useEffect(() => {
  //   const checkAuthStatus = async () => {
  //     const token = getCookieData("google-token");
  //     const expirationTime = getCookieData("google-token-expiration");
  //     const userData = getCookieData("userData");
  //     const user = userData ? JSON.parse(userData) : null;
  //     if (user) setUser(user);
  //     if (token && expirationTime) {
  //       const currentTime = new Date().getTime();
  //       if (currentTime < Number(expirationTime)) {
  //         const response = await fetch(
  //           `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${token}`,
  //         );
  //         if (response.ok) {
  //           const data: GoogleUser = await response.json();
  //           console.log(data, "data");
  //           if (data.email) {
  //             // getToken(data.email, token, false);
  //             setIsAuthenticated(true);
  //           } else {
  //             setIsAuthenticated(false);
  //             logout();
  //           }
  //         } else {
  //           logout();
  //         }
  //       } else {
  //         logout();
  //       }
  //     } else {
  //       setIsAuthenticated(false);
  //       setUser(defaultUserInfo);
  //     }
  //   };
  //   setTimeout(() => {
  //     checkAuthStatus();
  //   }, 1000);
  // }, [navigate, location]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        setUser,
        login,
        logout,
        getToken,
        loginTT,
       // testLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
